import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, Instagram, ExternalLink } from 'lucide-react';
import { useApp } from '@/state/AppContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ed722c14-f6d3-48d4-b5ee-3466358d0c74/artifacts/mwshbcey_BIG%20BASH%20LOGO.svg';

const LEGAL = [
  { label: 'Privacy Policy', url: 'https://raunakfoodcraft.com/privacy-policy/' },
  { label: 'Terms & Conditions', url: 'https://raunakfoodcraft.com/terms-conditions/' },
  { label: 'Disclaimer', url: 'https://raunakfoodcraft.com/disclaimer/' },
  { label: 'Return Policy', url: 'https://raunakfoodcraft.com/return-policy/' },
];

export const Footer = () => {
  const { config } = useApp();
  const wa = `https://wa.me/${config.whatsapp_number}?text=Hi%20Big%20Bash!`;
  const ig = `https://www.instagram.com/${config.instagram_handle || 'bigbash'}`;

  // Pretty-print the WhatsApp number e.g. "919321450920" -> "+91 93214 50920"
  const prettyWa = (() => {
    const n = String(config.whatsapp_number || '').replace(/\D/g, '');
    if (n.length === 12 && n.startsWith('91')) return `+91 ${n.slice(2, 7)} ${n.slice(7)}`;
    if (n.length === 10) return `+91 ${n.slice(0, 5)} ${n.slice(5)}`;
    return n ? `+${n}` : '';
  })();

  return (
    <footer className="mt-16 bg-[var(--bb-darkblue)] text-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="col-span-2 md:col-span-1">
          <div className="inline-flex items-center justify-center bg-white rounded-2xl p-2 mb-3">
            <img src={LOGO_URL} alt="Big Bash" className="h-10 w-auto" />
          </div>
          <p className="text-sm text-white/85 leading-relaxed font-nunito-medium">Fun, detailed, customisable chocolates for kids, parties, and corporate gifting.</p>
          <div className="mt-3 flex gap-2">
            <a href={ig} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] flex items-center justify-center" aria-label="Instagram">
              <Instagram className="w-4 h-4 text-white" />
            </a>
            <a href={wa} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] flex items-center justify-center" aria-label="WhatsApp">
              <MessageCircle className="w-4 h-4 text-white" />
            </a>
            <a href={`mailto:${config.support_email}`} className="w-9 h-9 rounded-full bg-[var(--bb-yellow)] hover:bg-[#e6ac00] flex items-center justify-center" aria-label="Email">
              <Mail className="w-4 h-4 text-[var(--bb-darkblue)]" />
            </a>
          </div>
        </div>
        <div>
          <div className="text-sm font-nunito-black mb-2 uppercase tracking-wider">Shop</div>
          <ul className="space-y-1.5 text-sm text-white/85 font-nunito-medium">
            <li><Link to="/shop?category=chocopops" className="hover:text-[var(--bb-yellow)] transition-colors">3D Choco Pops</Link></li>
            <li><Link to="/shop?category=chocosnaps" className="hover:text-[var(--bb-yellow)] transition-colors">Choco Snaps</Link></li>
            <li><Link to="/shop?category=chocodigits" className="hover:text-[var(--bb-yellow)] transition-colors">Choco Digits</Link></li>
            <li><Link to="/collections" className="hover:text-[var(--bb-yellow)] transition-colors">Collections</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-nunito-black mb-2 uppercase tracking-wider">Company</div>
          <ul className="space-y-1.5 text-sm text-white/85 font-nunito-medium">
            <li><Link to="/bulk-orders" className="hover:text-[var(--bb-yellow)] transition-colors">Bulk & Corporate</Link></li>
            <li><Link to="/support" className="hover:text-[var(--bb-yellow)] transition-colors">Customer Support</Link></li>
            <li><Link to="/profile" className="hover:text-[var(--bb-yellow)] transition-colors">Track an Order</Link></li>
            <li><a href={ig} target="_blank" rel="noreferrer" className="hover:text-[var(--bb-yellow)] transition-colors inline-flex items-center gap-1">Instagram <ExternalLink className="w-3 h-3" /></a></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-nunito-black mb-2 uppercase tracking-wider">Get in touch</div>
          <ul className="space-y-2 text-sm text-white/85 font-nunito-medium">
            {prettyWa && (
              <li>
                <a
                  href={wa}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-2 hover:text-[var(--bb-yellow)] transition-colors"
                  data-testid="footer-whatsapp-link"
                >
                  <span className="w-7 h-7 rounded-full bg-[var(--bb-cyan)] group-hover:bg-[var(--bb-cyan-deep)] flex items-center justify-center shrink-0">
                    <MessageCircle className="w-3.5 h-3.5 text-white" />
                  </span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-wider font-nunito-black text-white/70 leading-none">WhatsApp</span>
                    <span className="block font-nunito-black text-white leading-tight" data-testid="footer-whatsapp-number">{prettyWa}</span>
                  </span>
                </a>
              </li>
            )}
            <li>
              <a
                href={`mailto:${config.support_email}`}
                className="group inline-flex items-center gap-2 hover:text-[var(--bb-yellow)] transition-colors"
                data-testid="footer-email-link"
              >
                <span className="w-7 h-7 rounded-full bg-[var(--bb-yellow)] group-hover:bg-[#e6ac00] flex items-center justify-center shrink-0">
                  <Mail className="w-3.5 h-3.5 text-[var(--bb-darkblue)]" />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-wider font-nunito-black text-white/70 leading-none">Email</span>
                  <span className="block font-nunito-black text-white leading-tight break-all">{config.support_email}</span>
                </span>
              </a>
            </li>
            <li className="pt-1">
              <div className="text-[11px] uppercase tracking-wider font-nunito-black text-white/60 mb-1">Legal</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {LEGAL.map((l) => (
                  <a
                    key={l.url}
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-white/80 hover:text-[var(--bb-yellow)] transition-colors"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/15">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 text-xs text-white/70 font-nunito-medium flex flex-col md:flex-row gap-2 md:gap-4 justify-between">
          <span>&copy; {new Date().getFullYear()} Big Bash by Raunak Foodcraft. All rights reserved.</span>
          <span>FSSAI certified · Pan-India shipping · Edible inks</span>
        </div>
      </div>
    </footer>
  );
};
